import React, { useState, useEffect } from 'react';
import { Heart, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const response = await axios.get(`${API}/stories?status=active`);
      setStories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading stories:', error);
      setLoading(false);
    }
  };

  return (
    <div className="stories-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Patient Stories</h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Real stories from families we've had the privilege to care for. 
              Every story is a testament to courage, compassion, and the power of dignified care.
            </p>
          </div>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 mt-4">Loading stories...</p>
            </div>
          ) : stories.length === 0 ? (
            <div className="text-center py-12">
              <Heart size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">No stories available yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {stories.map((story) => (
                <Card key={story.id} className="border-none shadow-lg hover:shadow-xl transition-all group">
                  <CardContent className="p-0">
                    {/* Image */}
                    {story.imageUrl ? (
                      <div className="h-56 overflow-hidden rounded-t-lg">
                        <img 
                          src={story.imageUrl} 
                          alt={story.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="h-56 bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center rounded-t-lg">
                        <User size={64} className="text-blue-300" />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{story.name}</h3>
                      <p className="text-blue-600 font-semibold mb-4">{story.condition}</p>
                      <p className="text-gray-700 leading-relaxed line-clamp-6">{story.story}</p>
                      
                      {/* Read More Indicator */}
                      {story.story.length > 300 && (
                        <button className="text-blue-600 font-semibold mt-4 hover:text-blue-700 transition-colors">
                          Read full story →
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-500 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Your Story Matters</h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            If you'd like to share your experience with Dard Satya and help others find comfort, 
            we'd be honored to hear from you.
          </p>
          <a href="/contact" className="inline-block">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl">
              Share Your Story
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Stories;
